
import React, { useState } from 'react';
import { getImageUrl } from '@/utils/photoUtils';

interface WebPImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClass?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  fallbackSrc?: string;
  quality?: number;
  mode?: 'thumbnail' | 'full'; // New prop for image optimization mode
  useCloudflare?: boolean; // Deprecated - ImageKit.io is now used
}

export function WebPImage({ 
  src, 
  alt, 
  className, 
  containerClass,
  width, 
  height, 
  loading = 'lazy',
  decoding = 'async',
  onError,
  fallbackSrc = "https://picsum.photos/400/300",
  quality = 80,
  mode = 'full',
  useCloudflare = true
}: WebPImageProps) {
  const [imageSrc, setImageSrc] = useState(() => {
    console.log('🖼️ WebPImage initializing with src:', src, 'alt:', alt, 'mode:', mode, 'useCloudflare:', useCloudflare);
    
    // Use the provided src directly - photoUtils.getBestBusinessImageUrl handles all Cloudflare transformation
    return src || fallbackSrc;
  });

  const [hasTriedOriginal, setHasTriedOriginal] = useState(false);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    console.error('🚨 WebPImage: Image failed to load:', target.src);
    
    // If ImageKit.io URL failed, try to get original Supabase URL
    if (target.src.includes('ik.imagekit.io') && !hasTriedOriginal) {
      // Extract filename from ImageKit.io URL and construct original Supabase URL
      const match = target.src.match(/ik\.imagekit\.io\/goodvote\/[^\/]+\/(.+)$/);
      if (match) {
        const filename = match[1];
        const originalUrl = `https://nartcbzfahbmkgsysyur.supabase.co/storage/v1/object/public/business_images/${filename}`;
        console.log('🔄 WebPImage: ImageKit.io failed, falling back to original Supabase URL:', originalUrl);
        setImageSrc(originalUrl);
        setHasTriedOriginal(true);
        return;
      }
    }
    
    // If Cloudflare URL failed, try original Supabase URL
    if (target.src.includes('/cdn-cgi/image/') && !hasTriedOriginal) {
      // Extract original Supabase URL from Cloudflare transformation URL
      const match = target.src.match(/cdn-cgi\/image\/[^\/]+\/(https:\/\/[^?]+)/);
      if (match) {
        const originalUrl = decodeURIComponent(match[1]);
        console.log('🔄 WebPImage: Cloudflare failed, falling back to original Supabase URL:', originalUrl);
        setImageSrc(originalUrl);
        setHasTriedOriginal(true);
        return;
      } else {
        console.error('🚨 WebPImage: Could not extract original URL from Cloudflare URL:', target.src);
      }
    }
    
    // If current image failed and we haven't tried final fallback yet
    if (target.src !== fallbackSrc && !hasTriedFallback) {
      console.log('🔄 WebPImage: Original failed, falling back to fallback URL:', fallbackSrc);
      setImageSrc(fallbackSrc);
      setHasTriedFallback(true);
    } else {
      console.error('🚨 WebPImage: All fallback options exhausted for:', src);
    }
    
    onError?.(e);
  };

  const isCloudflareTransformed = imageSrc.includes('/cdn-cgi/image/');
  const isImageKitTransformed = imageSrc.includes('ik.imagekit.io');

  return (
    <div className={`relative ${containerClass || ''}`}>
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        className={className}
        onError={handleImageError}
        onLoad={() => {
          if (isCloudflareTransformed) {
            console.log('✅ WebPImage: Cloudflare image loaded successfully:', imageSrc);
          } else if (isImageKitTransformed) {
            console.log('✅ WebPImage: ImageKit.io image loaded successfully:', imageSrc);
          }
        }}
        crossOrigin="anonymous"
      />
      
      
      {/* Debug indicator for development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-1 left-1 bg-gray-500/80 text-white px-1 py-0.5 rounded text-xs">
          {mode}
        </div>
      )}
    </div>
  );
}
